import type { EntityCtx } from '@cli-types/next-adapter/generators.js'
import { toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { buildFormatActionDateSource } from '@next-adapter/generators/actions/action-helpers/build-format-action-date-source.js'
import { generateResolveMediaFunction } from './generate-resolve-media-function.js'
import { generateResolveSelectFieldsFunction } from './generate-resolve-select-fields-function.js'
import { generateSlugHelpers } from './generate-slug-helpers.js'
import { generateVersionSnapshotHelpers } from './generate-version-snapshot-helpers.js'

export function genHelpersContent(ctx: EntityCtx): string | null {
  const lines: string[] = []
  const drizzleImports = new Set<string>()
  const schemaImports = new Set<string>()

  if (ctx.hasM2M) {
    drizzleImports.add('eq')
    for (const field of ctx.m2mFields) {
      schemaImports.add(toCamelCase(`${ctx.singular}${toPascalCase(field.relationship || '')}`))
    }
  }
  if (ctx.hasListRels) {
    drizzleImports.add('inArray')
    for (const t of ctx.listRelTableImports) schemaImports.add(t)
  }
  if (ctx.hasMediaFields) {
    drizzleImports.add('inArray')
    schemaImports.add('adminMedia')
  }
  if (ctx.hasCreatableSelectFields) {
    schemaImports.add(ctx.selectOptionsTableVar)
  }
  if (ctx.hasSlug) {
    schemaImports.add(ctx.tableVar)
  }

  if (
    ctx.hasM2M ||
    ctx.hasListRels ||
    ctx.hasMediaFields ||
    ctx.hasCreatableSelectFields ||
    ctx.hasSlug
  ) {
    lines.push(`import db from '@admin/db'`)
  }
  const tables = [...schemaImports].sort()
  if (tables.length > 0) {
    lines.push(`import { ${tables.join(', ')} } from '@admin/db/schema'`)
  }
  if (drizzleImports.size > 0) {
    lines.push(`import { ${[...drizzleImports].sort().join(', ')} } from 'drizzle-orm'`)
  }
  if (ctx.hasMediaFields) {
    lines.push(`import type { AdminMedia } from '@admin/types'`)
  }
  if (ctx.hasSlug) {
    lines.push(`import { slugify } from '@admin/utils/validation/slugify'`)
  }
  const typeImports = ctx.hasSelectFields ? `${ctx.Singular}, SelectOptionValue` : ctx.Singular
  lines.push(`import type { ${typeImports} } from './types'`)
  lines.push('')
  lines.push(buildFormatActionDateSource({ exported: true }))
  lines.push('')

  if (ctx.hasSlug) {
    lines.push(generateSlugHelpers(ctx))
    lines.push('')
  }

  if (ctx.hasListRels) {
    const exportedFn = ctx.populateListRelsFn
      .replace('async function', 'export async function')
      .trim()
    lines.push(exportedFn)
    lines.push('')
  }

  if (ctx.hasMediaFields) {
    lines.push(generateResolveMediaFunction(ctx))
    lines.push('')
  }

  if (ctx.hasSelectFields) {
    lines.push(generateResolveSelectFieldsFunction(ctx))
    lines.push('')
  }

  lines.push(generateVersionSnapshotHelpers(ctx))
  lines.push('')

  return lines.join('\n')
}
